import React from "react";
import LegalLayout from "../components/LegalLayout";

const P = ({ children }) => (
  <p className="mb-4 text-slate-700">{children}</p>
);
const UL = ({ children }) => (
  <ul className="mb-4 list-disc space-y-1.5 pl-6 text-slate-700">{children}</ul>
);
const H3 = ({ children }) => (
  <h3 className="mb-2 mt-5 text-base font-semibold text-[#1e1b4b]">{children}</h3>
);

const sections = [
  {
    id: "overview",
    heading: "1. Overview",
    content: (
      <>
        <P>
          BuyerBeacon Pro ("we," "our," "BuyerBeacon Pro") provides data
          marketing services (the "Services") designed to help for-profit and
          not-for-profit organizations market their goods and services in a
          relevant and efficient way. Our solutions are used principally to
          support email and multi-channel marketing.
        </P>
        <P>
          We take the privacy interests of the individuals whose information we
          handle very seriously. This Privacy Policy explains how we use and
          manage information, and what rights consumers have to control how
          their information is used in marketing.
        </P>
        <P>
          California residents should also review the "Addendum for California
          Residents" at the end of this Policy.
        </P>
      </>
    ),
  },
  {
    id: "information-we-collect",
    heading: "2. Information We Collect",
    content: (
      <>
        <P>
          We receive data, including personal information, from a variety of
          sources — websites and apps that collect the information, public
          sources, and other data compilers ("Information"). Information in our
          database may include:
        </P>
        <UL>
          <li>Personal email and professional email addresses</li>
          <li>Email activity signals (recent use, marketing engagement)</li>
          <li>IP address</li>
          <li>Cookie or device identifiers</li>
          <li>Email and website interaction usage data</li>
          <li>
            Demographic information (inferred income range, age range, household
            size, home ownership)
          </li>
          <li>Consumer interest signals (product interests, hobbies, lifestyle)</li>
        </UL>
        <P>
          Some Information may be inferred (for example, an income range
          inferred from postal area). We may combine any of the points of
          Information collected with other Information.
        </P>
      </>
    ),
  },
  {
    id: "how-we-use",
    heading: "3. How We Use Information",
    content: (
      <>
        <H3>As part of our Services</H3>
        <UL>
          <li>
            <strong>Data Marketing Services</strong> — providing marketing
            intelligence to our customers, helping identify likely-interested
            (or disinterested) prospects.
          </li>
          <li>
            <strong>Retargeting Services</strong> — cross-referencing cookie
            identifiers and email addresses so customers can re-engage their
            visitors.
          </li>
          <li>
            <strong>Additional Services</strong> — campaign optimization,
            measurement, database analysis, fraud detection, and data hygiene.
          </li>
        </UL>
        <H3>To operate our Services</H3>
        <P>
          We use Information internally to improve, test, update, and verify
          our database; develop new products; and operate, analyze, secure, and
          improve our Services.
        </P>
      </>
    ),
  },
  {
    id: "sharing",
    heading: "4. How We Share Information",
    content: (
      <UL>
        <li>
          <strong>Customers</strong> — under license, as part of our Services.
        </li>
        <li>
          <strong>Business &amp; Data Partners</strong> — for tailored
          targeting, measurement, and analytics.
        </li>
        <li>
          <strong>Service Providers</strong> — hosting, customer support,
          marketing, data enhancement, security, and payments.
        </li>
        <li>
          <strong>Corporate Transfers</strong> — in connection with mergers,
          acquisitions, or asset sales.
        </li>
        <li>
          <strong>Affiliates, Parents, Subsidiaries</strong>.
        </li>
        <li>
          <strong>As Required by Law</strong> — subpoenas, court orders, or
          protecting the rights and safety of any person or entity.
        </li>
      </UL>
    ),
  },
  {
    id: "opt-out",
    heading: "5. Your Marketing & Opt-Out Choices",
    content: (
      <>
        <UL>
          <li>
            Visit{" "}
            <a
              href="https://buyerbeacon.pro/optout"
              className="text-[#1e1b4b] underline-offset-4 hover:underline"
            >
              buyerbeacon.pro/optout
            </a>{" "}
            to have your personal information removed from our marketing
            database. We will remove it from active marketing databases within
            fifteen (15) business days (subject to verification where
            reasonably required).
          </li>
          <li>
            To opt out of email marketing from a specific brand, use the
            "unsubscribe" link in the footer of the email.
          </li>
          <li>
            For direct mail preferences, visit the DMA Choice service at
            thedma.org.
          </li>
          <li>
            To opt out of retargeting, clear your browser cookies or use the
            universal opt-out URL above (per-browser).
          </li>
          <li>
            To unsubscribe from our own corporate marketing emails, use the
            "unsubscribe" link in any such email we send you.
          </li>
        </UL>
      </>
    ),
  },
  {
    id: "cookies",
    heading: "6. Cookies and Similar Technologies",
    content: (
      <P>
        BuyerBeacon Pro and its business partners use industry-standard
        technologies — including cookies, pixel tags, and similar identifiers —
        for tailoring marketing, measuring engagement, and operating our
        Services. Most browsers accept cookies by default; disabling cookies
        may impair functionality of certain features.
      </P>
    ),
  },
  {
    id: "corporate-website",
    heading: "7. Data Collected Through Our Website",
    content: (
      <>
        <P>
          When you interact with our corporate website, we collect: domain
          name, IP address, browser/user-agent metadata, page-engagement
          signals, and any information you submit (forms, sign-ups, requests).
          We use this Information to:
        </P>
        <UL>
          <li>Create and manage user accounts</li>
          <li>Provide Services you request</li>
          <li>Respond to and communicate with you</li>
          <li>Send offers, retargeting messages, and partner offers</li>
          <li>Perform analytics and market research</li>
          <li>Combine with other data to improve service relevance</li>
          <li>Diagnose technical issues and enforce our Terms</li>
        </UL>
      </>
    ),
  },
  {
    id: "links",
    heading: "8. Links to Other Sites",
    content: (
      <P>
        This site may link to third-party websites. BuyerBeacon Pro is not
        responsible for the privacy practices of those sites.
      </P>
    ),
  },
  {
    id: "security",
    heading: "9. Security and Data Integrity",
    content: (
      <P>
        BuyerBeacon Pro implements physical and electronic safeguards including
        firewalls, encryption, hashing/truncation of data, and access controls.
        No platform, however, can guarantee 100% safety from intrusion. See our{" "}
        <a href="/security" className="text-[#1e1b4b] underline-offset-4 hover:underline">
          Security
        </a>{" "}
        page for additional detail.
      </P>
    ),
  },
  {
    id: "changes",
    heading: "10. Changes to This Privacy Policy",
    content: (
      <P>
        We may update this Privacy Policy from time to time. Any changes become
        effective when posted to our website. Please check back periodically.
      </P>
    ),
  },
  {
    id: "us-storage",
    heading: "11. Storage of Information in the United States",
    content: (
      <P>
        Information is processed and stored on servers located in the United
        States. If you access our Services from outside the U.S., your data may
        be subject to laws with different privacy standards than those in your
        home country.
      </P>
    ),
  },
  {
    id: "california",
    heading: "12. California Privacy Rights (CCPA / CPRA)",
    content: (
      <>
        <P>
          This Addendum supplements the Privacy Policy and applies only to
          California residents ("California Consumers").
        </P>
        <H3>Categories of Personal Information We Collect</H3>
        <UL>
          <li>Identifiers (email, IP, cookie/device IDs)</li>
          <li>Commercial information (purchase signals, interests)</li>
          <li>Internet / network activity (browsing, email engagement)</li>
          <li>Geolocation (general, e.g., postal area)</li>
          <li>Inferences (preferences, characteristics)</li>
        </UL>
        <H3>Sources</H3>
        <P>
          Websites and apps, public records, data compilers, our customers, and
          you directly.
        </P>
        <H3>Business Purposes</H3>
        <P>
          Services delivery, marketing, analytics, fraud detection, legal
          compliance, security.
        </P>
        <H3>Your California Rights</H3>
        <UL>
          <li>Right to know what personal information we collect, use, share</li>
          <li>
            Right to delete personal information (subject to legal retention
            exceptions)
          </li>
          <li>Right to opt out of "sale" or "sharing"</li>
          <li>Right to non-discrimination for exercising these rights</li>
          <li>Right to correct inaccurate information</li>
          <li>Right to limit use of sensitive personal information</li>
        </UL>
        <P>
          To exercise these rights, visit{" "}
          <a
            href="https://buyerbeacon.pro/optout"
            className="text-[#1e1b4b] underline-offset-4 hover:underline"
          >
            buyerbeacon.pro/optout
          </a>{" "}
          or email{" "}
          <a
            href="mailto:admin@buyerbeacon.pro"
            className="text-[#1e1b4b] underline-offset-4 hover:underline"
          >
            admin@buyerbeacon.pro
          </a>
          . We will respond within forty-five (45) days (extendable by an
          additional 45 days where reasonably necessary).
        </P>
      </>
    ),
  },
];

export default function PrivacyPage() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Privacy Policy"
      lastUpdated="February 2026"
      path="/privacy"
      seoDescription="BuyerBeacon Pro Privacy Policy — how we collect, use, and protect personal information. CCPA, CPRA, and GDPR compliant. Contact admin@buyerbeacon.pro."
      sections={sections}
    />
  );
}
